/**
 * League Card Component
 * Reusable component for displaying league summary in a card format
 * Handles format badge logic correctly for all 4 league types
 */

import { League } from "../types/index.js";
import { t } from "../i18n/config";

export class LeagueCard {
  static render(league: League, showManagement: boolean = false): string {
    const getFormatInfo = (format: string): { type: string; system: string } => {
      switch (format) {
        case 'swiss':
          return { type: t('league.type.pairs'), system: t('league.format.swiss') };
        case 'roundrobin':
          return { type: t('league.type.pairs'), system: t('league.format.roundRobin') };
        case 'individual_swiss':
          return { type: t('league.type.individual'), system: t('league.format.swiss') };
        case 'individual_roundrobin':
          return { type: t('league.type.individual'), system: t('league.format.roundRobin') };
        default:
          return { type: t('league.type.unknown'), system: t('league.format.unknown') };
      }
    };

    const getFormatBadgeColor = (_format: string): string => {
      return 'badge-primary';
    };

    const renderFooter = () => {
      if (showManagement) {
        return `
          <div class="mt-auto flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-800 daylight:border-day-ink/10">
            <button class="btn-primary flex-1 view-league-btn group-hover:bg-primary/80" data-league-id="${league.id}" onclick="event.stopPropagation(); window.location.hash = '#league/${league.id}'">
              <span class="material-symbols-outlined">arrow_forward</span>
              <span>${t('leagueCard.view')}</span>
            </button>
            ${league.sharingToken ? `
              <button class="btn-secondary copy-link-btn" data-share-token="${league.sharingToken}" title="${t('leagueCard.copyLink')}">
                <span class="material-symbols-outlined">share</span>
              </button>
            ` : ""}
            <button class="btn-secondary settings-league-btn" data-league-id="${league.id}" title="${t('leagueCard.settings')}">
              <span class="material-symbols-outlined">settings</span>
            </button>
            ${!league.isArchived ? `
              <button class="btn-secondary archive-league-btn" data-league-id="${league.id}" title="${t('leagueCard.archive')}">
                <span class="material-symbols-outlined">archive</span>
              </button>
            ` : `
              <button class="btn-secondary unarchive-league-btn" data-league-id="${league.id}" title="${t('leagueCard.unarchive')}">
                <span class="material-symbols-outlined">unarchive</span>
              </button>
            `}
            <button class="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition delete-league-btn" data-league-id="${league.id}" title="${t('leagueCard.delete')}">
              <span class="material-symbols-outlined text-sm">delete</span>
            </button>
          </div>
        `;
      }

      return `
        <div class="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800 daylight:border-day-ink/10 mt-auto">
          <div class="flex items-center gap-4 text-xs font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
            <div class="flex items-center gap-1" title="${t('leagueCard.players')}">
              <span class="material-symbols-outlined text-base">groups</span>
              <span>${(league as any).enrollments?.length || (league as any).playerCount || 0}</span>
            </div>
            <div class="flex items-center gap-1" title="${t('leagueCard.rounds')}">
              <span class="material-symbols-outlined text-base">calendar_month</span>
              <span>${(league as any).rounds?.length || (league as any).roundCount || 0}</span>
            </div>
          </div>
          
          <button class="btn-ghost p-2 h-8 w-8 rounded-full flex items-center justify-center text-slate-400 daylight:text-day-ink-muted hover:text-primary daylight:hover:text-day-ink hover:bg-primary/10" onclick="event.stopPropagation(); window.location.hash = '#league/${league.id}'">
            <span class="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
      `;
    };

    const formatInfo = getFormatInfo(league.format);

    return `
      <div class="card hover:shadow-md transition-all duration-200 group cursor-pointer h-full flex flex-col" onclick="window.location.hash = '#league/${league.id}'">
        <div class="flex justify-between items-start mb-4">
          <div class="flex items-center gap-3 overflow-hidden">
            <div class="w-10 h-10 rounded-lg bg-primary/10 daylight:bg-primary/20 flex items-center justify-center text-primary daylight:text-day-ink group-hover:bg-primary group-hover:text-navy-800 transition-colors">
              <span class="material-symbols-outlined">trophy</span>
            </div>
            <div class="min-w-0">
              <h3 class="font-bold text-slate-900 dark:text-white daylight:text-day-ink leading-tight truncate group-hover:text-primary daylight:group-hover:text-day-ink transition-colors" title="${league.name}">${league.name}</h3>
              <span class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">ID: #${league.id}</span>
            </div>
          </div>
          <div class="flex flex-col items-end gap-1 ml-2 shrink-0">
            <span class="badge ${getFormatBadgeColor(league.format)} whitespace-nowrap">
              ${formatInfo.type}
            </span>
            <span class="badge bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10 whitespace-nowrap text-[10px] h-5 px-2">
              ${formatInfo.system}
            </span>
          </div>
        </div>
        
        <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-4 line-clamp-2 flex-grow">
          ${league.description || t('leagueCard.noDescription')}
        </p>

        ${renderFooter()}
      </div>
    `;
  }
}